/**
 * 화면 계획 버전 채널 — 버전 드롭다운이 고른 계획을 챗에 알린다.
 *
 * 시나리오 플래닝 화면은 어댑터(buildContext)가 plan_ver_id 를 직접 채우지만, 리포트·검토
 * 화면(자원 생산 계획·리더보드 등)은 어댑터가 없어 챗이 화면의 버전을 몰랐다. 그러면 백엔드가
 * 릴리즈 기준으로 답한다 — 사용자가 수립 중 버전을 보고 있어도.
 *
 * 왜 redux viewStates 를 읽지 않는가: 화면마다 저장 시점이 다르다. ResourceProductionPlan 은
 * 조회 완료 .then() 에서만 써서, 버전만 바꾸고 조회하지 않으면 낡은 값이 남는다 — 사고 화면에서
 * 정확히 틀린다. 드롭다운이 선택 즉시 발행하는 값이 유일하게 화면과 같다.
 *
 * 규약
 *  - 발행은 moduleCd 별 슬롯 하나. 뒤에 마운트된 선택기가 앞의 것을 덮는다.
 *  - 언마운트 시 retract 는 토큰을 대조한다 — 늦게 사라지는 옛 선택기가 새 선택기의 값을
 *    지우지 못하게.
 *  - 읽기는 챗 모듈 코드로 한다. BF 는 자기 버전이 없어 화면 드롭다운이 DP 버전을 고르므로
 *    (DpVersionSelect 가 moduleCd:"DP" 로 조회) BF → DP 별칭으로 읽는다.
 *  - 모달의 참조 버전 선택기(소스 데이터·상위 계획 연결)는 발행하지 않는다 — 그 선택은 화면이
 *    보고 있는 계획이 아니다(publishToChat={false}).
 *  - 구독할 수 있다. 챗 패널의 버전 칩이 드롭다운 변경을 바로 따라간다(useScreenVersions).
 *    스냅샷은 내용이 바뀔 때만 새로 만든다. useSyncExternalStore 는 같은 상태에서 같은 참조를 요구한다.
 *  - 요청에는 챗 모듈 슬롯(screenVersionFields)과 별개로 모든 슬롯(screenVersionsPayload)을 싣는다.
 *    RP 화면이 고른 DP 버전처럼 챗 모듈과 다른 모듈의 버전도 백엔드가 알아야 한다.
 */

import { useSyncExternalStore } from "react";

// 챗 모듈 코드 → 발행 슬롯 모듈. 자기 버전이 없는 모듈만 적는다.
const MODULE_ALIAS = { BF: "DP" };

// 칩과 요청에 싣는 슬롯과 그 순서. 백엔드 infra/plan_version.SCREEN_VERSION_MODULES 와 같다.
const SLOT_ORDER = ["DP", "IP", "RP", "MP"];

const slots = new Map();   // moduleCd → { token, version }
let seq = 0;

const listeners = new Set();
let snapshot = Object.freeze([]);

const norm = (moduleCd) => String(moduleCd ?? "").trim().toUpperCase();

const sameVersion = (a, b) =>
  a.module === b.module && a.id === b.id && a.name === b.name && a.statusCd === b.statusCd;

// 슬롯이 바뀐 뒤 부른다. 내용이 같으면 스냅샷 참조를 유지하고 알리지 않는다.
const refresh = () => {
  const next = SLOT_ORDER.filter((m) => slots.has(m))
    .map((m) => Object.freeze({ module: m, ...slots.get(m).version }));
  if (next.length === snapshot.length && next.every((v, i) => sameVersion(v, snapshot[i]))) return;
  snapshot = Object.freeze(next);
  listeners.forEach((fn) => fn());
};

/** 선택기가 고른 버전을 싣는다. 돌려주는 토큰으로만 회수할 수 있다. */
export const publishScreenVersion = (moduleCd, version) => {
  const mod = norm(moduleCd);
  if (!mod || version?.id == null) return null;
  const token = ++seq;
  slots.set(mod, {
    token,
    version: { id: version.id, name: version.name ?? "", statusCd: version.statusCd ?? "" },
  });
  refresh();
  return token;
};

/** 토큰이 아직 슬롯의 주인일 때만 비운다. */
export const retractScreenVersion = (moduleCd, token) => {
  const mod = norm(moduleCd);
  const cur = slots.get(mod);
  if (cur && cur.token === token) {
    slots.delete(mod);
    refresh();
  }
};

/** 슬롯 변화 구독. 해지 함수를 돌려준다. */
export const subscribeScreenVersions = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

/** 발행된 버전 목록 [{module, id, name, statusCd}] (DP·IP·RP·MP 순, 동결). */
export const getScreenVersionsSnapshot = () => snapshot;

/** 버전 칩용 구독 훅. */
export const useScreenVersions = () =>
  useSyncExternalStore(subscribeScreenVersions, getScreenVersionsSnapshot);

/** 챗 요청에 실을 모든 슬롯의 버전. 없으면 빈 객체(키 자체를 만들지 않는다). */
export const screenVersionsPayload = () => (snapshot.length ? {
  screen_versions: snapshot.map((v) => ({
    module: v.module, plan_ver_id: v.id, plan_ver_name: v.name, plan_ver_status: v.statusCd,
  })),
} : {});

/** 챗 모듈이 볼 화면 버전. 없으면 null. */
export const readScreenVersion = (chatModule) => {
  const mod = norm(chatModule);
  const slot = slots.get(MODULE_ALIAS[mod] ?? mod);
  return slot ? slot.version : null;
};

/** 챗 요청 컨텍스트에 실을 필드. 없으면 빈 객체(키 자체를 만들지 않는다). */
export const screenVersionFields = (chatModule) => {
  const v = readScreenVersion(chatModule);
  return v ? { plan_ver_id: v.id, plan_ver_name: v.name, plan_ver_status: v.statusCd } : {};
};
